<template>
  <div class="chat-container">
    <section class="chat-content">
      <ChatLoading v-if="fullLoadingView" />
      <div v-else class="chat-content" ref="refChatRoom">
        <div :class="domainClasses.container">
          <SingleMessageAreaAway />
          <div :class="domainClasses.divider" />
          <SingleMessageAreaHome :domain="domain" />
        </div>
      </div>
      <AudioPlayer />
      <SecretController v-if="isVisibleSecretController" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, provide, Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { storeToRefs } from 'pinia';

import SecretController from '@/components/domains/chat/chat-general/SecretController.vue';
import SingleMessageAreaAway from '@/components/domains/chat/chat-single/SingleMessageAreaAway.vue';
import SingleMessageAreaHome from '@/components/domains/chat/chat-single/SingleMessageAreaHome.vue';
import AudioPlayer from '@/components/domains/chat/common/AudioPlayer.vue';
import ChatLoading from '@/components/domains/chat/common/ChatLoading.vue';
import { useMessagesSingle } from '@/composables/domains/chat/useMessagesSingle';
import { useSocketSingle } from '@/composables/domains/chat/useSocketSingle';
import { useAccount } from '@/composables/shared/useAccount';
import { useNotification } from '@/composables/shared/useNotification';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { QRCodeType } from '@/stores/chat';
import { SocketState, useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { useUserStore } from '@/stores/user';
import { UserType } from '@/stores/user';
import { Language, TypeDomain } from '@/types/common';
import { isInCapacitorApp } from '@/utils/device';
import {
  ij_bottomBarStatus,
  ij_isVisibleSecretController,
  ij_languageAway,
  ij_languageHome,
  ij_popBubbleEmpty,
  ij_setBottomBarStatus,
  ij_setEmptyBubbleMy,
  ij_setEmptyBubbleOther,
} from '@/utils/injectionKeys.js';
import { addAppStateChangeListener } from '@/utils/native/nativeBridge';
import redirectToLoginPage from '@/utils/redirectToLoginPage';

const router = useRouter();
const socketStore = useSocketStore();
const callStore = useCallStore();
const audioStore = useAudioStore();
const toastStore = useToastStore();
const userStore = useUserStore();

const props = withDefaults(
  defineProps<{
    domain?: TypeDomain;
  }>(),
  {
    domain: 'CHAT',
  }
);

const setBottomBarStatus = inject(ij_setBottomBarStatus) as (status: number) => void;
const bottomBarStatus = inject(ij_bottomBarStatus, ref(0));
const languageHome = inject(ij_languageHome) as Ref<Language>;
const languageAway = inject(ij_languageAway) as Ref<Language>;
const isVisibleSecretController = inject(ij_isVisibleSecretController);

const {
  isGuestMicMuted,
  attendees,
  recover,
  evicted,
  isFastInterpretation,
  selectedChatId,
  connectionResponse,
  socketState,
} = storeToRefs(socketStore);
const { t } = useI18n();
const { logoutAndGoToLogin } = useAccount();

const fullLoadingView = ref(true);

const refChatRoom = ref();
const showTutorial = ref(false);
const isWaitingToRetry = ref<boolean>(false);
const retryCount = ref<number>(0);
const isMobile = isInCapacitorApp();
let tryConnection = false;

const domainClasses = computed<Record<string, string>>(() => {
  return (
    {
      CHAT: {
        container: 'chat-message-container',
        divider: 'divider-small',
      },
      SELF_SERVICE: {
        container: 'chat-selfservice-message-container',
        divider: 'selfservice-divider-small',
      },
    }[props.domain] || {
      container: 'chat-message-container',
      divider: 'divider-small',
    }
  );
});

// 나, 상대 언어 세트 (소켓 연결용)
const languageSet = computed(() => {
  return { languageHome: languageHome.value, languageAway: languageAway.value };
});

const initProcess = async () => {
  if (!audioStore.devices) {
    await audioStore.fetchDevices();
  }

  socketStore.exceededUsageLimit = false;

  if (!socketStore.channel) {
    socketStore.channel = new BroadcastChannel('connect_ws');
  }

  if (socketStore.socket?.readyState !== WebSocket.OPEN) {
    await connectToWebsocket(false, languageSet.value);
  } else {
    if (bottomBarStatus.value !== -2 && bottomBarStatus.value !== 0) {
      setBottomBarStatus(0);
    }
  }
};

const onAppStateChange = async (isActive: boolean) => {
  if (isActive) {
    await connectToWebsocket(true, languageSet.value);
  }
};

const { alertNoEmployee, alertEvictMyself, alertExceededUsageLimit, alertAlreadyEndedCallWithReconnect, alertBasic } =
  useNotification();

const { popBubbleEmpty, setEmptyBubbleMy, setEmptyBubbleOther, resetUseMessagesCall, handleWebSocketMessage } =
  useMessagesSingle({
    refChatRoom,
    attendees,
    recover,
    evicted,
    socketState,
    selectedChatId,
    isGuestMicMuted,
    connectionResponse,
    isFastInterpretation,
    setAttendees: socketStore.setAttendees,
    findAttendeeNickname: socketStore.findAttendeeNickname,
    addAttendeeByUserId: socketStore.addAttendeeByUserId,
    removeAttendeeByUserId: socketStore.removeAttendeeByUserId,
    sendMessageAttendees: socketStore.sendMessageAttendees,
  });
const { connectToWebsocket } = useSocketSingle();

provide(ij_popBubbleEmpty, popBubbleEmpty);
provide(ij_setEmptyBubbleMy, setEmptyBubbleMy);
provide(ij_setEmptyBubbleOther, setEmptyBubbleOther);

onMounted(async () => {
  if (socketStore.connectionResponse === 9999) return connectToWebsocket(false, languageSet.value);
  //Call Accepted
  if (socketStore.connectionResponse === 2) return initProcess();

  const tutorialSeen = localStorage.getItem('tutorialSeen');
  if (!tutorialSeen) showTutorial.value = true;

  if (isMobile) {
    await addAppStateChangeListener(onAppStateChange);
  }
});

onUnmounted(() => {
  resetUseMessagesCall();
  if (isMobile) {
    window._onAppStateChange = undefined;
  }
});

// 소켓 연결 상태
watch(
  () => socketStore.socketState,
  async (state) => {
    const isOpen = state && SocketState.isOpen(state);
    const isClosed = SocketState.isClosed(state);

    const reconnectAfterDelay = () => {
      setTimeout(async () => {
        if (bottomBarStatus.value === 1) {
          await connectToWebsocket(false, languageSet.value);
          retryCount.value += 1;
          isWaitingToRetry.value = false;
        }
      }, 3000);
    };

    const handleRetryOrExit = () => {
      if (!isWaitingToRetry.value) {
        isWaitingToRetry.value = true;
        setBottomBarStatus(1);
        reconnectAfterDelay();
        if (retryCount.value === 0) {
          setTimeout(() => {
            if (state === SocketState.CLOSED) {
              toastStore.error(t('try_reconnection'), 'RECONNECT');
            }
          }, 3000);
        }
      }
    };

    // OPEN 상태
    if (isOpen) {
      // 두 번째 watcher의 메시지 핸들러 설정
      socketStore.socket!.onmessage = (event) => handleWebSocketMessage(event.data);

      fullLoadingView.value = false;

      // 첫 번째 watcher의 연결 성공 처리
      socketStore.sendMessageChangeLanguage(languageHome.value?.code, languageAway.value?.code);
      setTimeout(() => {
        if (socketStore.socketState === SocketState.OPEN) {
          setTimeout(() => {
            socketStore.sendMessageAttendees();
          }, 3000);

          retryCount.value = 0;

          if (bottomBarStatus.value !== -2) setBottomBarStatus(0);
        }
      }, 500);
    }
    // CLOSED 상태
    else if (isClosed) {
      if (tryConnection) {
        // 첫 번째 watcher의 마이크 종료 & 재시도 로직
        if (audioStore.isMicOn) audioStore.toggleMic(false);
        if (bottomBarStatus.value !== 2) {
          handleRetryOrExit();
        }
      } else {
        // 두 번째 watcher의 최초 재연결 로직
        if (fullLoadingView.value === true && !tryConnection) {
          await connectToWebsocket(false, languageSet.value);
          tryConnection = true;
        }
      }
    }
    // 재시도 실패
    else if (state === SocketState.RETRY_FAILED) {
      setBottomBarStatus(-1);
    }
    // 헤더 없는 에러
    else if (state === SocketState.ERROR_NO_HEADER) {
      if (socketStore.socket!.readyState !== WebSocket.OPEN) {
        connectToWebsocket(false, languageSet.value);
      }
      socketStore.sendMessageAudioStreamStart();
    }
  },
  { immediate: true }
);

watch(
  () => socketStore.connectionResponse,
  async (response) => {
    console.log(`Micinput connectionResponse: ${response}`);

    // 연결 시도 또는 연결 성공
    if ([-1, 0, 1].includes(response)) return;

    // 연결 수립 이후 초기화
    if (response === 2) {
      await initProcess();
      return;
    }

    // 그 외 모든 경우는 소켓 종료
    socketStore.close();

    const isConsultation = QRCodeType.isConsultationType(callStore.qrCodeInfo.qrCodeType);
    const isPartner = UserType.isPartner(callStore.userInfo.userType);
    switch (response) {
      case 1016: // 잘못된 코드 요청
        setBottomBarStatus(2);
        localStorage.setItem('errorCode', String(response));
        router.push({ name: 'chat-ended' });
        break;

      case 1029: // 사용량 초과
        setBottomBarStatus(1);
        alertExceededUsageLimit(() => {
          if (props.domain === 'CHAT') {
            redirectToLoginPage();
          } else {
            socketStore.close();
          }
        });
        break;

      case 1030: // 동시 접속시
        setBottomBarStatus(2);
        alertEvictMyself(() => redirectToLoginPage()); // 싱글 로그인
        break;

      case 4000:
        alertBasic({ title: t('wrong_approach') }, logoutAndGoToLogin);
        break;

      case 4010: // 인증 실패
        userStore.requestLogout();
        redirectToLoginPage();
        break;

      case 1026: // 이미 통역 중일 때 or 동시 접속자 초과
        setBottomBarStatus(isConsultation ? 1 : 2);
        localStorage.setItem('errorCode', String(response));
        if (!isConsultation) {
          localStorage.setItem('chatId', socketStore.selectedChatId);
        }
        router.push({ name: 'chat-max-exceeded' });
        break;

      case 1027: // 이미 종료된 통역일 때
        setBottomBarStatus(2);
        if (!isConsultation && isPartner) {
          alertAlreadyEndedCallWithReconnect(connectToWebsocket(false, languageSet.value));
        } else {
          localStorage.setItem('errorCode', String(response));
          router.push({ name: 'chat-ended' });
        }
        break;

      case 1023: // 호스트 접속이 안되어있을 때 (FAIR / CONFERENCE)
        if (!isConsultation) {
          setBottomBarStatus(2);
          alertNoEmployee(() =>
            router.push({
              name: 'chat-entrance',
              params: { code: callStore.qrCodeInfo.code },
            })
          );
        }
        break;

      default: // 나머지 에러
        setBottomBarStatus(-1);
        break;
    }
  }
);
</script>

<style scoped lang="scss">
@import '@/assets/sass/layout/_selfservice.scss';

.chat-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: space-between;
  overflow: hidden;
}

.chat-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.chat-message-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  max-width: 75rem;
  margin: 0 auto;
  flex-grow: 1;
}

.selfservice-divider-small {
  height: 1px;
  margin: 0.5rem 1rem;
  background-color: var(--border-default);
  flex-shrink: 0;
}
</style>
